import React from 'react';
import cn from '@/utils/cn';
import { SPANavSlot } from '@/components/admin/SysadminSPA/shared/SPANav';
import type { PromptListItem } from './types';

interface Props {
  isLoading: boolean;
  onSelect: (name: string) => void;
  prompts: PromptListItem[];
  selectedName: string | null;
}

const rowClass = (active: boolean) => cn(
  'tw-flex tw-w-full tw-flex-col tw-gap-0.5 tw-rounded-md tw-border-0 tw-px-3 tw-py-1.5',
  'tw-text-left tw-text-sm tw-cursor-pointer',
  active
    ? 'tw-bg-spa-accent-bg tw-text-spa-accent-text-hover tw-font-medium'
    : 'tw-bg-transparent tw-text-spa-primary hover:tw-bg-spa-bg-tertiary',
);

const latestVersion = (versions: number[]) => (versions.length > 0 ? Math.max(...versions) : null);

const LoadingRows = () => (
  <div aria-hidden className="tw-flex tw-flex-col tw-gap-2 tw-px-2 tw-py-2">
    {[0, 1, 2, 3, 4, 5].map(row => (
      <div key={row} className="tw-h-10 tw-animate-pulse tw-rounded-md tw-bg-spa-bg-skeleton" />
    ))}
  </div>
);

// AIDEV-NOTE: The prompt list is the page's drill rail (SPANavSlot), like DevDocs and Content
// Audit: selection state stays on the page and only the list renders inside the shell rail.
const PromptList = ({ isLoading, onSelect, prompts, selectedName }: Props) => (
  <SPANavSlot header={{ backLabel: 'Langfuse', backTo: '/langfuse', title: 'Prompt Management' }}>
    {isLoading ? <LoadingRows /> : (
      <nav aria-label="Prompts" className="tw-px-2 tw-pt-1 tw-pb-3">
        <div
          className={cn(
            'tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider',
            'tw-text-spa-secondary',
          )}
        >
          Prompts ({prompts.length})
        </div>
        {prompts.length === 0 && (
          <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-spa-secondary">No prompts found.</div>
        )}
        {prompts.map((prompt) => {
          const active = selectedName === prompt.name;
          const latest = latestVersion(prompt.versions);
          return (
            <button
              key={prompt.name}
              aria-current={active ? 'true' : undefined}
              className={rowClass(active)}
              type="button"
              onClick={() => onSelect(prompt.name)}
            >
              <span className="tw-truncate">{prompt.name}</span>
              <span
                className={cn(
                  'tw-flex tw-items-center tw-gap-2 tw-text-xs',
                  active ? 'tw-text-spa-accent-text' : 'tw-text-spa-secondary',
                )}
              >
                <span>{prompt.type}</span>
                {latest !== null && <span>v{latest}</span>}
                {prompt.labels?.includes('production') && (
                  <span className="tw-font-medium tw-text-spa-success">prod</span>
                )}
              </span>
            </button>
          );
        })}
      </nav>
    )}
  </SPANavSlot>
);

export default PromptList;
